import React, { useState } from 'react';

import { FilterRange, InfoTooltip, Input } from '@hcs/design-system';
import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { SpatialSortField } from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import {
  formatNumber,
  formatSqFt,
  numInputStrStrip,
  unformat,
} from '@hcs/utils';

// Common styles for living area types
import styles from '../livingArea/livingArea.module.css';

const FIELD = PropertyStateFields.livingArea;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Square Feet';
const labelShort = 'GLA';
const propertyStatePath: PropertyStatePaths = '/propertyDetails/livingArea';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyState?.propertyDetails?.livingArea;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatSqFt(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'ft²';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatNumber(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)} ${labelShort}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateEditProps<typeof FIELD>) => {
  const defaultValue = getValue(propertyStateArgs);
  const [value, setValue] = useState(
    defaultValue === null ? null : formatNumber(defaultValue),
  );

  return (
    <Input
      dataHcName="gla-adjust-input"
      ariaLabel={`Adjust ${label}`}
      className={className}
      value={value?.toString() || ''}
      onChange={(value) => {
        const valueOrNull = value === '' ? null : numInputStrStrip(value);
        setValue(valueOrNull);

        onChange({
          path: propertyStatePath,
          value: valueOrNull === null ? valueOrNull : Number(value),
          field: FIELD,
        });
      }}
      onFocus={() => {
        if (value !== null) {
          setValue(unformat(value).toString());
        }
      }}
      onBlur={() => {
        if (value !== null) {
          setValue(formatNumber(Number(value)));
        }
      }}
      maxLength={25}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
  hideLabels,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <FilterRange
      className={className}
      disabled={disabled}
      hideLabels={hideLabels}
      maxVal={999999}
      initialValue={{
        min: spatialFiltersInput.minSqft
          ? formatNumber(spatialFiltersInput.minSqft)
          : '',
        max: spatialFiltersInput.maxSqft
          ? formatNumber(spatialFiltersInput.maxSqft)
          : '',
      }}
      onBlur={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            minSqft: value.min === '' ? null : Number(value.min),
            maxSqft: value.max === '' ? null : Number(value.max),
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return (
    <TableHeaderCell {...props}>
      <span className={styles.HeaderCellLabel}>GLA (ft²)</span>
      <InfoTooltip
        size="small"
        dataHcName="gross-living-area-info-tooltip"
        isLargeTooltip
        label={'Gross Living Area'}
      />
    </TableHeaderCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      {formatNumber(getValue(propertyStateArgs))}
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const LIVING_AREA_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  labelMicro,
  formatValueShort,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
  Filter,
  spatialSortField: SpatialSortField.Sqft,
};
